import '@store/processors/watchers';
import { state as checkoutState, dispose as disposeCheckout } from '@store/checkout';
import { state as processorsState, dispose as disposeProcessors } from '@store/processors';
import { state as selectedProcessor, dispose as disposeSelectedProcessor } from '@store/selected-processor';
import { Checkout, PaymentMethodType, Processor } from '../../../types';

const processor = (processor_type: string, overrides: Partial<Processor> = {}) =>
  ({ id: `${processor_type}id`, live_mode: false, recurring_enabled: true, processor_type, processor_data: {}, ...overrides } as unknown as Processor);

/** What clicking a payment tile does — see sc-payment-method-choice `onScShow`. `id` fires its watcher before `method` is set. */
const clickTile = (id: string, method?: string) => {
  selectedProcessor.id = id;
  selectedProcessor.manual = false;
  selectedProcessor.method = method;
};

/**
 * Replace the checkout the way a PATCH response does. The checkout store compares by
 * `JSON.stringify`, so an identical object would not fire the watchers; `updated_at` keeps each one distinct.
 */
let updatedAt = 0;
const replaceCheckout = (overrides: Partial<Checkout> = {}) => {
  checkoutState.checkout = { id: 'checkout_1', live_mode: false, currency: 'usd', payment_method_required: true, updated_at: ++updatedAt, ...overrides } as Checkout;
};

describe('processors watchers', () => {
  beforeEach(() => {
    disposeCheckout();
    disposeProcessors();
    disposeSelectedProcessor();
    checkoutState.formId = 1;
    checkoutState.mode = 'test';
  });

  it('keeps the paypal card method when the checkout is replaced', () => {
    processorsState.processors = [processor('paypal')];
    replaceCheckout();
    clickTile('paypal', 'card');
    expect(selectedProcessor.method).toBe('card');

    // Any checkout PATCH (email, address, coupon) replaces `checkoutState.checkout`.
    replaceCheckout();
    expect(selectedProcessor.method).toBe('card');
  });

  it('clears the method when the processor is switched from under it', () => {
    processorsState.processors = [processor('stripe'), processor('paypal', { recurring_enabled: false })];
    replaceCheckout();
    clickTile('paypal', 'card');
    expect(selectedProcessor.method).toBe('card');

    // The checkout turns recurring, paypal is no longer available, and the store auto-switches to stripe.
    replaceCheckout({ reusable_payment_method_required: true });
    expect(selectedProcessor.id).toBe('stripe');
    expect(selectedProcessor.method).toBeNull();
  });

  it('drops the paypal card method once another card processor comes back and its tile is gone', () => {
    processorsState.processors = [processor('paypal'), processor('paystack', { recurring_enabled: false })];
    // A subscription hides paystack, so paypal is the only card option and sc-payment renders its Credit Card tile.
    replaceCheckout({ reusable_payment_method_required: true });
    clickTile('paypal', 'card');
    expect(selectedProcessor.method).toBe('card');

    // Subscription removed: paystack is back, the tile unmounts, and a stale 'card' would leave no tile open.
    replaceCheckout({ reusable_payment_method_required: false });
    expect(selectedProcessor.id).toBe('paypal');
    expect(selectedProcessor.method).toBeNull();
  });

  it('re-validates a method-aware processor method against the available methods', () => {
    processorsState.processors = [processor('razorpay')];
    processorsState.methods = [{ id: 'card' }, { id: 'upi' }] as PaymentMethodType[];
    replaceCheckout();
    clickTile('razorpay', 'upi');
    expect(selectedProcessor.method).toBe('upi');

    // The checkout turns recurring; upi is still offered, so it stays.
    replaceCheckout({ reusable_payment_method_required: true });
    expect(selectedProcessor.method).toBe('upi');

    // The razorpay provider re-fetches recurring methods and upi is gone.
    processorsState.methods = [{ id: 'card' }] as PaymentMethodType[];
    expect(selectedProcessor.method).toBe('card');

    // A method that is no longer offered is also corrected on the next checkout change.
    selectedProcessor.method = 'upi';
    replaceCheckout({ reusable_payment_method_required: true });
    expect(selectedProcessor.method).toBe('card');
  });
});
